import React, {
  ComponentPropsWithoutRef,
  useEffect,
  useCallback,
  useState,
  useMemo,
  useRef,
} from "react";
import ReactDOM from "react-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { LinkPreview } from "./LinkPreview";
import { Copy, Download, Code, BarChart, Maximize2, X, ZoomIn, ZoomOut, RotateCcw } from "lucide-react";
import { notify } from "../../utils/toast";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { vs, okaidia } from "react-syntax-highlighter/dist/esm/styles/prism";
import { useTheme } from "../../hooks";

// Get the mermaid config based on theme
const getMermaidConfig = (isDark: boolean) => {
  return {
    startOnLoad: false,
    theme: isDark ? "dark" : "default",
    securityLevel: "loose" as "loose",
    flowchart: {
      htmlLabels: true,
      curve: 'basis' as 'basis'
    },
    sequence: {
      showSequenceNumbers: false,
      actorMargin: 80,
      messageMargin: 40
    },
    er: {
      layoutDirection: 'TB' as 'TB',
      minEntityWidth: 100,
      minEntityHeight: 75
    },
    gitGraph: {
      showBranches: true,
      showCommitLabel: true
    }
  };
};

interface CodeBlockProps {
  language: string;
  value: string;
}

const getInitialSystemTheme = () => {
  if (typeof window === "undefined") return "light" as const;
  return window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
};

const CodeBlock: React.FC<CodeBlockProps> = ({ language, value }) => {
  const { theme } = useTheme();
  const [copied, setCopied] = useState(false);
  const [systemTheme, setSystemTheme] = useState<"dark" | "light">(
    getInitialSystemTheme()
  );

  // Listen for system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handleChange = (e: MediaQueryListEvent) => {
      setSystemTheme(e.matches ? "dark" : "light");
    };

    setSystemTheme(mediaQuery.matches ? "dark" : "light");
    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  // Determine the effective theme
  const effectiveTheme = theme === "system" ? systemTheme : theme;

  const handleCopy = () => {
    navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="relative group rounded-lg overflow-hidden my-6">
      <div className="absolute right-3 top-3 z-10 opacity-0 group-hover:opacity-100 transition-opacity">
        <button
          onClick={handleCopy}
          className={`px-3 py-1.5 text-sm font-medium rounded-md shadow-sm hover:bg-opacity-90 transition-colors ${
            effectiveTheme === "dark"
              ? "bg-gray-700 text-gray-100 hover:bg-gray-600"
              : "bg-gray-200 text-gray-800 hover:bg-gray-300"
          }`}
        >
          {copied ? "Copied!" : "Copy"}
        </button>
      </div>
      <div
        className={`${
          effectiveTheme === "dark" ? "bg-gray-900" : "bg-gray-50"
        } rounded-lg p-5 text-[15px] leading-relaxed`}
      >
        <SyntaxHighlighter
          language={language}
          style={effectiveTheme === "dark" ? okaidia : vs}
          PreTag="div"
          useInlineStyles={true}
          customStyle={{
            margin: 0,
            padding: 0,
            background: "none",
            fontSize: "15px",
            lineHeight: "1.6",
          }}
        >
          {value}
        </SyntaxHighlighter>
      </div>
    </div>
  );
};

// Mermaid diagram component
const MermaidDiagram: React.FC<{ code: string }> = ({ code }) => {
  const { theme } = useTheme();
  const [systemTheme, setSystemTheme] = useState<"dark" | "light">(
    getInitialSystemTheme()
  );
  const [showCode, setShowCode] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [fullScreenZoom, setFullScreenZoom] = useState(1);
  const diagramRef = useRef<HTMLDivElement>(null);
  const diagramId = useRef(`mermaid-${Math.random().toString(36).substring(2, 10)}`);
  const lastRenderKey = useRef<string | null>(null);
  const isRendering = useRef(false);
  const currentSvgRef = useRef<SVGElement | null>(null);
  const diagramCode = code.trim();

  // Listen for system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handleChange = (e: MediaQueryListEvent) => {
      setSystemTheme(e.matches ? "dark" : "light");
    };

    setSystemTheme(mediaQuery.matches ? "dark" : "light");
    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  // Determine the effective theme
  const effectiveTheme = useMemo(
    () => (theme === "system" ? systemTheme : theme),
    [theme, systemTheme]
  );

  const applySvgSizing = useCallback((svg: SVGElement, maxHeight = "600px") => {
    svg.style.width = "100%";
    svg.style.height = "auto";
    svg.style.maxWidth = "100%";
    svg.style.maxHeight = maxHeight;
    svg.setAttribute("preserveAspectRatio", "xMidYMid meet");
  }, []);

  const applyZoomToSvg = useCallback((svg: SVGElement | null, scale: number) => {
    if (!svg) return;
    svg.style.transformOrigin = "top left";
    svg.style.transform = `scale(${scale})`;
  }, []);

  useEffect(() => {
    applyZoomToSvg(currentSvgRef.current, zoom);
  }, [applyZoomToSvg, zoom]);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(diagramCode);
      notify.success("Diagram code copied to clipboard");
    } catch (error) {
      console.error("Copy error:", error);
      notify.error("Failed to copy diagram code");
    }
  }, [diagramCode]);

  const handleDownload = useCallback(() => {
    try {
      if (!diagramRef.current) {
        notify.error("Diagram not ready for download");
        return;
      }

      // Find SVG element
      const svgElement = diagramRef.current.querySelector("svg");
      if (!svgElement) {
        notify.error("No SVG found in diagram");
        return;
      }

      // Clone SVG to avoid modifying the displayed one
      const svgClone = svgElement.cloneNode(true) as SVGElement;
      const svgData = new XMLSerializer().serializeToString(svgClone);
      const svgBlob = new Blob([svgData], { type: "image/svg+xml" });
      const url = URL.createObjectURL(svgBlob);
      
      const link = document.createElement("a");
      link.href = url;
      link.download = `diagram-${diagramId.current}.svg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download error:", error);
      notify.error("Failed to download diagram");
    }
  }, []);

  const renderMermaid = useCallback(() => {
    if (!diagramRef.current || showCode) return;

    const renderKey = `${diagramCode}|${effectiveTheme}`;
    if (
      isRendering.current ||
      (lastRenderKey.current === renderKey && diagramRef.current.childElementCount > 0)
    ) {
      return;
    }
    lastRenderKey.current = renderKey;

    const renderDiagram = async () => {
      try {
        isRendering.current = true;
        // Clear previous content
        diagramRef.current!.innerHTML = '';
        
        // Dynamically import mermaid
        const mermaidModule = await import('mermaid');
        const mermaid = mermaidModule.default;
        
        // Initialize mermaid with the correct theme
        mermaid.initialize({
          ...getMermaidConfig(effectiveTheme === "dark"),
          theme: effectiveTheme === "dark" ? "dark" : "default",
          logLevel: 1
        });
        
        // Add the mermaid element
        const mermaidDiv = document.createElement('div');
        mermaidDiv.className = 'mermaid';
        mermaidDiv.textContent = diagramCode;
        diagramRef.current!.appendChild(mermaidDiv);
        
        // Process the diagram
        await mermaid.run({
          nodes: [mermaidDiv],
          suppressErrors: false,
        });
        
        // Apply dark mode styles if needed
        if (effectiveTheme === "dark") {
          const svg = diagramRef.current!.querySelector('svg');
          if (svg) {
            currentSvgRef.current = svg;
            applySvgSizing(svg);
            applyZoomToSvg(svg, zoom);

            const texts = svg.querySelectorAll('text, .messageText, .actor, .noteText, tspan');
            texts.forEach((text) => {
              text.setAttribute('color', '#000000');
            });
          }
        } else {
          const svg = diagramRef.current!.querySelector('svg');
          if (svg) {
            currentSvgRef.current = svg;
            applySvgSizing(svg);
            applyZoomToSvg(svg, zoom);
          }
        }
      } catch (error) {
        console.error("Mermaid rendering error:", error);
        diagramRef.current!.innerHTML = `
          <div class="p-4 border rounded border-red-300 bg-red-50 dark:bg-red-900/20 dark:border-red-800 text-red-700 dark:text-red-300">
            <p class="font-medium">Error rendering diagram</p>
            <p class="text-sm mt-1 font-mono">${String(error).replace(/</g, '&lt;').replace(/>/g, '&gt;')}</p>
          </div>
        `;
      }
      isRendering.current = false;
    };

    renderDiagram();
  }, [applySvgSizing, applyZoomToSvg, diagramCode, effectiveTheme, showCode]);

  // Render mermaid on mount and when theme changes
  useEffect(() => {
    renderMermaid();
  }, [renderMermaid]);

  // Handle toggling between diagram and code views
  useEffect(() => {
    if (!diagramRef.current) return;

    if (showCode) {
      diagramRef.current.innerHTML = "";
      lastRenderKey.current = null;
      isRendering.current = false;
      currentSvgRef.current = null;
      setZoom(1);
    } else {
      renderMermaid();
    }
  }, [renderMermaid, showCode]);
  
  const toggleFullScreen = useCallback(() => {
    setIsFullScreen((prev) => !prev);
  }, []);

  useEffect(() => {
    if (isFullScreen) {
      setFullScreenZoom(1);
    }
  }, [isFullScreen]);

  // Add event listener for ESC key to exit full screen
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isFullScreen) {
        setIsFullScreen(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isFullScreen]);

  // Create a full screen diagram component that will be rendered in a portal
  const FullScreenDiagram = () => {
    if (!isFullScreen) return null;
    
    // Create a ref for the fullscreen diagram
    const fullScreenDiagramRef = useRef<HTMLDivElement>(null);
    const fullScreenSvgRef = useRef<SVGElement | null>(null);
    const fullScreenRenderKey = useRef<string | null>(null);
    const isRenderingFullScreen = useRef(false);

    useEffect(() => {
      applyZoomToSvg(fullScreenSvgRef.current, fullScreenZoom);
    }, [applyZoomToSvg, fullScreenZoom]);
    
    // Rerender the diagram when fullscreen is shown
    useEffect(() => {
      if (fullScreenDiagramRef.current) {
        const renderKey = `${diagramCode}|${effectiveTheme}`;
        if (
          isRenderingFullScreen.current ||
          (fullScreenRenderKey.current === renderKey && fullScreenDiagramRef.current.childElementCount > 0)
        ) {
          return;
        }
        fullScreenRenderKey.current = renderKey;

        const renderFullScreenDiagram = async () => {
          try {
            isRenderingFullScreen.current = true;
            // Clear previous content
            fullScreenDiagramRef.current!.innerHTML = '';
            
            // Dynamically import mermaid
            const mermaidModule = await import('mermaid');
            const mermaid = mermaidModule.default;
            
            // Initialize mermaid with the correct theme
            mermaid.initialize({
              ...getMermaidConfig(effectiveTheme === "dark"),
              theme: effectiveTheme === "dark" ? "dark" : "default"
            });
            
            // Add the mermaid element
            const mermaidDiv = document.createElement('div');
            mermaidDiv.className = 'mermaid';
            mermaidDiv.textContent = diagramCode;
            fullScreenDiagramRef.current!.appendChild(mermaidDiv);
            
            // Process the diagram
            await mermaid.run({
              nodes: [mermaidDiv]
            });
            
            // Apply dark mode styles if needed
            if (effectiveTheme === "dark") {
              const svg = fullScreenDiagramRef.current!.querySelector('svg');
              if (svg) {
                fullScreenSvgRef.current = svg;
                applySvgSizing(svg, "calc(100vh - 140px)");
                applyZoomToSvg(svg, fullScreenZoom);
                const texts = svg.querySelectorAll('text, .messageText, .actor, .noteText, tspan');
                texts.forEach((text) => {
                  text.setAttribute('color', '#000000');
                });
              }
            } else {
              const svg = fullScreenDiagramRef.current!.querySelector('svg');
              if (svg) {
                fullScreenSvgRef.current = svg;
                applySvgSizing(svg, "calc(100vh - 140px)");
                applyZoomToSvg(svg, fullScreenZoom);
              }
            }
          } catch (error) {
            console.error("Mermaid fullscreen rendering error:", error);
          }
          isRenderingFullScreen.current = false;
        };

        renderFullScreenDiagram();
      }
    }, [applySvgSizing, applyZoomToSvg, diagramCode, effectiveTheme]);

    return ReactDOM.createPortal(
      <div className="fixed inset-0 z-[9999] bg-white dark:bg-gray-900 p-4 flex flex-col">
        <div className="flex justify-end items-center gap-2 mb-4">
          <div className="flex gap-1 rounded-md bg-gray-100 dark:bg-gray-800 p-1 shadow-sm">
            <button
              onClick={() => setFullScreenZoom((z) => Math.max(0.5, Math.round((z - 0.1) * 100) / 100))}
              className="p-2 rounded-md hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors text-gray-700 dark:text-gray-300"
              title="Zoom out"
            >
              <ZoomOut size={18} />
            </button>
            <button
              onClick={() => setFullScreenZoom(1)}
              className="p-2 rounded-md hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors text-gray-700 dark:text-gray-300"
              title="Reset zoom"
            >
              <RotateCcw size={18} />
            </button>
            <button
              onClick={() => setFullScreenZoom((z) => Math.min(3, Math.round((z + 0.1) * 100) / 100))}
              className="p-2 rounded-md hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors text-gray-700 dark:text-gray-300"
              title="Zoom in"
            >
              <ZoomIn size={18} />
            </button>
          </div>
          <button
            onClick={toggleFullScreen}
            className="p-2 rounded-md bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 transition-colors text-gray-700 dark:text-gray-300"
            title="Exit full screen"
          >
            <X size={20} />
          </button>
        </div>
        <div className="flex-grow flex items-center justify-center w-full overflow-auto">
          <div
            ref={fullScreenDiagramRef}
            className="max-w-full max-h-full w-full h-full overflow-auto"
          />
        </div>
      </div>,
      document.body
    );
  };

  return (
    <>
      <FullScreenDiagram />
      <div className="relative mt-8">
        <div className="absolute -top-8 right-0 z-10 flex gap-2 p-1 rounded-md bg-gray-100 dark:bg-gray-800 shadow-sm">
          <button
            onClick={handleCopy}
            className="p-1.5 rounded-md hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors text-gray-700 dark:text-gray-300"
            title="Copy diagram code"
          >
            <Copy size={16} />
          </button>
          {!showCode && (
            <button
              onClick={handleDownload}
              className="p-1.5 rounded-md hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors text-gray-700 dark:text-gray-300"
              title="Download diagram"
            >
              <Download size={16} />
            </button>
          )}
          {!showCode && (
            <>
              <button
                onClick={() => setZoom((z) => Math.max(0.5, Math.round((z - 0.1) * 100) / 100))}
                className="p-1.5 rounded-md hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors text-gray-700 dark:text-gray-300"
                title="Zoom out"
              >
                <ZoomOut size={16} />
              </button>
              <button
                onClick={() => setZoom(1)}
                className="p-1.5 rounded-md hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors text-gray-700 dark:text-gray-300"
                title="Reset zoom"
              >
                <RotateCcw size={16} />
              </button>
              <button
                onClick={() => setZoom((z) => Math.min(3, Math.round((z + 0.1) * 100) / 100))}
                className="p-1.5 rounded-md hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors text-gray-700 dark:text-gray-300"
                title="Zoom in"
              >
                <ZoomIn size={16} />
              </button>
            </>
          )}
          <button
            onClick={() => setShowCode(!showCode)}
            className="p-1.5 rounded-md hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors text-gray-700 dark:text-gray-300"
            title={showCode ? "Show diagram" : "Show code"}
          >
            {showCode ? <BarChart size={16} /> : <Code size={16} />}
          </button>
          {!showCode && (
            <button
              onClick={toggleFullScreen}
              className="p-1.5 rounded-md hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors text-gray-700 dark:text-gray-300"
              title="Full screen"
            >
              <Maximize2 size={16} />
            </button>
          )}
        </div>

        <div
          ref={diagramRef}
          className="mt-2 w-full overflow-auto rounded-xl bg-gray-50/70 dark:bg-gray-900/40 min-h-[240px] max-h-[600px] border border-gray-200/60 dark:border-gray-700/50 p-4"
          hidden={showCode}
        />

        {showCode && (
          <div className="relative group rounded-lg overflow-hidden my-6">
            <div
              className={`${
                effectiveTheme === "dark" ? "bg-gray-900" : "bg-gray-50"
              } rounded-lg p-5 text-[15px] leading-relaxed`}
            >
              <SyntaxHighlighter
                language="mermaid"
                style={effectiveTheme === "dark" ? okaidia : vs}
                PreTag="div"
                useInlineStyles={true}
                customStyle={{
                  margin: 0,
                  padding: 0,
                  background: "none",
                  fontSize: "15px",
                  lineHeight: "1.6",
                }}
              >
                {diagramCode}
              </SyntaxHighlighter>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

interface TypeWriterProps {
  content: string;
}

const TypeWriter: React.FC<TypeWriterProps> = ({ content }) => {
  const [displayedContent, setDisplayedContent] = useState("");
  const [isMounted, setIsMounted] = useState(false);
  const contentRef = React.useRef(content);

  useEffect(() => {
    setIsMounted(true);
    return () => setIsMounted(false);
  }, []);

  useEffect(() => {
    if (!isMounted) return;

    if (!content) {
      setDisplayedContent("");
      contentRef.current = "";
      return;
    }

    if (content !== contentRef.current) {
      setDisplayedContent(content);
      contentRef.current = content;
    }
  }, [content, isMounted]);

  if (!displayedContent) {
    return (
      <div
        className="prose dark:prose-invert max-w-none"
        style={{
          fontFamily:
            "ui-sans-serif, -apple-system, system-ui, 'Segoe UI', Helvetica, 'Apple Color Emoji', Arial, sans-serif, 'Segoe UI Emoji', 'Segoe UI Symbol'",
        }}
      ></div>
    );
  }

  return <MarkdownContent key={content} content={displayedContent} />;
};

interface MarkdownContentProps {
  content: string;
}

// Define the MarkdownContent component
// Function to extract and process Mermaid diagrams before rendering
const processMermaidBlocks = (content: string): string => {
  // Pattern to match: "mermaid flowchart TB" or similar Mermaid syntax blocks
  // This handles cases where the code block might not be properly formatted with ```mermaid
  const mermaidPattern = /(^|\n)(mermaid\s+(?:graph|flowchart|sequenceDiagram|classDiagram|stateDiagram|erDiagram|journey|gantt|pie|flowchart)\s[^\n]*[\s\S]*?)(\n\n|\n$|$)/g;
  
  // Replace direct mermaid syntax blocks with proper markdown code blocks
  let processedContent = content.replace(mermaidPattern, (match, pre, mermaidCode, post) => {
    return `${pre}\`\`\`mermaid\n${mermaidCode.replace(/^mermaid\s+/, '')}\n\`\`\`${post}`;
  });

  // Also look for code blocks that contain Mermaid syntax but don't have the language specifier
  const codeBlockPattern = /```(?!\w)([\s\S]*?)```/g;
  processedContent = processedContent.replace(codeBlockPattern, (match, codeContent) => {
    if (codeContent.trim().match(/^(?:graph|flowchart|sequenceDiagram|classDiagram|stateDiagram|erDiagram|journey|gantt|pie|flowchart)\s/)) {
      return "```mermaid" + codeContent + "```";
    }
    return match;
  });

  return processedContent;
};

const MarkdownContent: React.FC<MarkdownContentProps> = ({ content }) => {
  const { theme } = useTheme();
  const [systemTheme, setSystemTheme] = useState<"dark" | "light">("light");
  
  // Process content to properly format mermaid blocks
  const processedContent = React.useMemo(() => processMermaidBlocks(content), [content]);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handleChange = (e: MediaQueryListEvent) => {
      setSystemTheme(e.matches ? "dark" : "light");
    };

    setSystemTheme(mediaQuery.matches ? "dark" : "light");
    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  const effectiveTheme = React.useMemo(
    () => (theme === "system" ? systemTheme : theme),
    [theme, systemTheme]
  );

  return (
    <article
      className="prose dark:prose-invert max-w-none break-words"
      style={{
        fontFamily:
          "ui-sans-serif, -apple-system, system-ui, 'Segoe UI', Helvetica, 'Apple Color Emoji', Arial, sans-serif, 'Segoe UI Emoji', 'Segoe UI Symbol'",
        overflowWrap: "break-word",
        wordBreak: "break-word",
        hyphens: "auto",
      }}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          // Make all links open in a new tab with preview
          a: ({ node, href, children, ...props }) => {
            // Only apply link preview to http/https links
            if (
              href &&
              (href.startsWith("http://") || href.startsWith("https://"))
            ) {
              return <LinkPreview href={href}>{children}</LinkPreview>;
            }

            // For other links (like anchors), just open in new tab
            return (
              <a
                href={href}
                {...props}
                target="_blank"
                rel="noopener noreferrer"
              >
                {children}
              </a>
            );
          },
          p: (props: ComponentPropsWithoutRef<"p">) => (
            <span className="block mb-4 last:mb-0">{props.children}</span>
          ),
          code: ({
            inline,
            className,
            children,
            ...props
          }: ComponentPropsWithoutRef<"code"> & { inline?: boolean }) => {
            // Handle mermaid diagrams
            if (className === "language-mermaid") {
              return <MermaidDiagram code={String(children).trim()} />;
            }

            // Handle regular code blocks
            const match = /language-(\w+)/.exec(className || "");
            const language = match ? match[1] : "";
            const code = String(children || "").replace(/\n$/, "");

            if (!inline && language) {
              return <CodeBlock language={language} value={code} />;
            }

            return inline ? (
              <code className="bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded text-[15px] font-medium">
                {children}
              </code>
            ) : (
              <div
                className={`${
                  effectiveTheme === "dark" ? "bg-gray-900" : "bg-gray-50"
                } rounded-lg p-5 text-[15px] leading-relaxed`}
              >
                <SyntaxHighlighter
                  language={language || "text"}
                  style={effectiveTheme === "dark" ? okaidia : vs}
                  PreTag="div"
                  useInlineStyles={true}
                  customStyle={{
                    margin: 0,
                    padding: 0,
                    background: "none",
                    fontSize: "15px",
                    lineHeight: "1.6",
                  }}
                >
                  {code}
                </SyntaxHighlighter>
              </div>
            );
          },
        }}
      >
        {processedContent}
      </ReactMarkdown>
    </article>
  );
};

interface MessageContentProps {
  content: string;
  isLiveResponse: boolean;
  type?: string;
}

// Create a memoized version of MarkdownContent
const MemoizedMarkdownContent = React.memo(
  MarkdownContent,
  (prevProps, nextProps) => prevProps.content === nextProps.content
);

// Define the MessageContent component
export const MessageContent: React.FC<MessageContentProps> = ({
  content,
  isLiveResponse,
  type,
}) => {
  if (type === "qr") {
    return <div dangerouslySetInnerHTML={{ __html: content }} />;
  }

  if (isLiveResponse) {
    return <TypeWriter content={content} />;
  }

  return <MemoizedMarkdownContent content={content} />;
};

// Create a memoized version of MessageContent
export const MemoizedMessageContent = React.memo(
  MessageContent,
  (prevProps, nextProps) =>
    prevProps.content === nextProps.content &&
    prevProps.isLiveResponse === nextProps.isLiveResponse &&
    prevProps.type === nextProps.type
);
